import React, { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
interface FAQItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
  index: number;
  isInView: boolean;
}
function FAQItem({
  question,
  answer,
  isOpen,
  onToggle,
  index,
  isInView
}: FAQItemProps) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.5,
    delay: 0.1 * index
  }} className="bg-white rounded-xl border border-[#3D2B1F]/8 overflow-hidden" data-id="element-11">
      <button onClick={onToggle} className="w-full flex items-center justify-between p-5 md:p-6 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C75B2A] focus-visible:ring-inset" aria-expanded={isOpen} data-id="element-12">
        <span className="text-lg font-semibold text-[#3D2B1F] pr-4" data-id="element-13">
          {question}
        </span>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.3
      }} className="flex-shrink-0" data-id="element-14">
          <ChevronDownIcon className="w-5 h-5 text-[#8B7565]" aria-hidden="true" data-id="element-15" />
        </motion.div>
      </button>
      <AnimatePresence initial={false} data-id="element-16">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} data-id="element-17">
            <div className="px-5 md:px-6 pb-5 md:pb-6" data-id="element-18">
              <p className="text-[#5C4A3A] text-base leading-relaxed" data-id="element-19">
                {answer}
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}
export function FAQSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const handleToggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  return <section ref={ref} className="w-full bg-[#F5EDE4] px-4 py-16 md:py-24" data-id="element-20">
      <div className="max-w-3xl mx-auto" data-id="element-21">
        <motion.h2 initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-3xl md:text-4xl font-bold text-[#3D2B1F] text-center mb-10 leading-tight" data-id="element-22">
          Preguntas Frecuentes
        </motion.h2>

        <div className="space-y-3" data-id="element-23">
          <FAQItem question="¿Funciona con niños 'rebeldes'?" answer="Sí, porque trabaja con la biología del cerebro, no solo con el comportamiento superficial. El protocolo actúa sobre el sistema nervioso — tuyo y el de tu hijo — para desactivar la escalada emocional antes de que se salga de control." isOpen={openIndex === 0} onToggle={() => handleToggle(0)} index={0} isInView={isInView} data-id="element-24" />
          <FAQItem question="¿Es muy difícil de aprender?" answer="No. La técnica principal toma menos de un minuto entenderla y se fortalece con la práctica diaria. No necesitas estudiar psicología ni leer cientos de páginas. Es un protocolo de emergencia diseñado para padres ocupados." isOpen={openIndex === 1} onToggle={() => handleToggle(1)} index={1} isInView={isInView} data-id="element-25" />
          <FAQItem question="¿Cómo recibo el material?" answer="Al ser digital, lo recibes en tu correo segundos después de tu compra para leerlo en tu celular o tablet. No tienes que esperar envíos ni descargar aplicaciones complicadas. Abres el correo y empiezas." isOpen={openIndex === 2} onToggle={() => handleToggle(2)} index={2} isInView={isInView} data-id="element-26" />
        </div>
      </div>
    </section>;
}